import { createColumnHelper } from '@tanstack/react-table';
import { ConfirmModal, createDataCollectionContext, dbTimeToUserTime, toaster, useMobileContext, type ConfirmModalHandle, type ItemAction } from '@shared';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Flex, Text } from '@chakra-ui/react';
import contactImportsService from '../../../../api/services/contactImportsService';
import { useMeta } from '../../../../api/hooks/useMeta';
import { useDrawer, useGetAllGroups } from '@admin';
import ImportDrawer from './ImportDrawer';
import { useQueryClient } from '@tanstack/react-query';
import { useDeleteMultipleContactImports } from '../../../../api/hooks/useContactImports';
import { IMPORT_POLL_INTERVAL, isImportRunning } from './contactImportUtils';

const columnHelper = createColumnHelper<ContactImport>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export const Imports = () => {
	const { t } = useTranslation();
	const { meta } = useMeta('contactImportsService');
	const { isMobile } = useMobileContext();
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<ContactImport>(),
		[],
	);
	const { data: groupData } = useGetAllGroups();

	const columns = [
		columnHelper.accessor('name', {
			header: t('general.name'),
			enableSorting: true,
		}),
		columnHelper.accessor('importStatus', {
			header: t('general.status'),
			cell: ({ row }) => meta?.importStatus?.[row.original.importStatus],
			enableSorting: true,
		}),
		columnHelper.accessor('successCount', {
			header: t('general.successful_imports'),
			enableSorting: true,
		}),
		columnHelper.accessor('skippedCount', {
			header: t('general.skipped_imports'),
			enableSorting: true,
		}),
		columnHelper.accessor('failedCount', {
			header: t('general.failed_imports'),
			enableSorting: true,
		}),
		columnHelper.accessor('errorMessage', {
			header: t('general.error_message'),
			enableSorting: true,
		}),
		columnHelper.accessor('addToGroupIDs', {
			header: t('general.add_to_groups'),
			enableSorting: true,
			cell: ({ row }) => {
				const groups = groupData?.data.groups.filter(g => row.original.addToGroupIDs?.map(id => parseInt(id)).includes(g.groupID));
				return <Text>{groups?.map(group => group.name).join(', ')}</Text>;
			},
		}),
		columnHelper.accessor('defaultPhoneCountry', {
			header: t('contacts.default_country_code'),
			enableSorting: true,
		}),
		columnHelper.accessor('isFirstRowHeader', {
			header: t('contacts.is_first_row_header'),
			cell: ({ row }) => {
				return t(`general.${row.original.isFirstRowHeader}`);
			},
			enableSorting: true,
		}),
		columnHelper.accessor('isImportingNewOnly', {
			header: t('contacts.is_importing_new_only'),
			enableSorting: true,
			cell: ({ row }) => {
				return t(`general.${row.original.isImportingNewOnly}`);
			},
		}),
		columnHelper.accessor('originalFileName', {
			header: t('general.original_file_name'),
			enableSorting: true,
		}),
		columnHelper.accessor('propertyUpdatingMethod', {
			header: t('contacts.property_updating_method'),
			cell: ({ row }) => meta?.propertyUpdatingMethod?.[row.original.propertyUpdatingMethod],
			enableSorting: true,
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			enableSorting: true,
			sortingFn: 'basic',
			cell: ({ row }) => {
				return dbTimeToUserTime(row.original.timeCreated, TIMEZONE);
			},
		}),
	];

	const { openDrawer } = useDrawer();

	const confirmRemoveRef = useRef<ConfirmModalHandle>(null);
	const { mutate: deleteMultipleContactImports } = useDeleteMultipleContactImports();
	const queryClient = useQueryClient();

	const selectedItemsActions: Array<ItemAction<ContactImport>> = [
		{
			id: 'deleteMany',
			label: t('general.delete_selected'),
			callbackFn: async (ids, table) => {
				const payload = {
					contactImports: ids.map(id => {
						return {
							contactImportID: parseInt(id),
							delete: true,
						};
					}),
				};

				const response = await confirmRemoveRef.current?.open({
					title: t('general.remove_imports'),
					content: t('general.really_remove_imports'),
					confirmButtonText: t('general.remove'),
					confirmButtonVariant: 'subtle',
				});

				if (response === true) {
					deleteMultipleContactImports(payload, {
						onSuccess: () => {
							toaster.create({
								type: 'success',
								title: t('general.removed'),
							});
							queryClient.invalidateQueries({ queryKey: ['contactImports'] });
							table.resetRowSelection();
						},
					});
				}
			},
		},
	];

	return (
		<Card.Root flex={1} display="flex">
			<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
				<DataCollectionContextProvider
					dataKey="contactImports"
					keyAttribute="contactImportID"
					emptyDataText={t('general.no_imports_found')}
					fetchFn={(...args) => contactImportsService.getAllResources(...args)}
					queryParams={{
						scopeWith: 'successCount|skippedCount|failedCount',
					}}
					initialItemsPerPage={25}
					columns={columns}
					isServerSide={false}
					isSelectable={true}
					defaultSort={{ id: 'timeCreated', desc: true }}
					defaultHiddenColumns={['defaultPhoneCountry', 'isFirstRowHeader', 'isImportingNewOnly', 'propertyUpdatingMethod']}
					onClickRow={row => {
						openDrawer({
							id: 'importDrawer',
							size: 'xl',
							content: <ImportDrawer importID={row.original.contactImportID} />,
						});
					}}
					selectedItemsActions={selectedItemsActions}
					refetchInterval={data =>
						data?.data.contactImports?.some(i => isImportRunning(i.importStatus)) ? IMPORT_POLL_INTERVAL : false
					}
				>
					<Flex gap={2} flexWrap="wrap" flexShrink={0}>
						<DataCollectionTableHeader />
					</Flex>
					<DataCollectionTable />
					<Flex gap={2}>
						<DataCollectionTableFooter />
						<ConfirmModal ref={confirmRemoveRef} />
					</Flex>
				</DataCollectionContextProvider>
			</Card.Body>
		</Card.Root>
	);
};
